import { useEffect } from 'react';
import {
  createRootRouteWithContext,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { PostHogProvider, usePostHog } from '@posthog/react';
import posthog from 'posthog-js';
import type { RouterContext } from '../router';
import { POSTHOG_API_KEY } from '@/lib/posthogClient';
import { setLogSink } from '@/lib/logger';
import { MathJaxProvider } from '@/components/MathJaxProvider';
import { CommandsProvider } from '@/commands';

/**
 * Routes the structured logger (`@/lib/logger`) into PostHog's logs API. The
 * logger stays a vendor-free leaf; the PostHog coupling lives here, where the
 * client is already initialized and the browser context (needed for the
 * SDK's own CORS-friendly ingest) exists — so it never leaks into workers.
 *
 * `LogLevel` (`debug|info|warn|error`) is a subset of PostHog's
 * `LogSeverityLevel`, so `record.level` maps straight through.
 */
function PostHogLoggerBridge() {
  const posthog = usePostHog();
  useEffect(() => {
    setLogSink((record) =>
      posthog.captureLog({
        body: record.message,
        level: record.level,
        attributes: record.attributes,
      }),
    );
    return () => setLogSink(null);
  }, [posthog]);
  return null;
}

function RootComponent() {
  return (
    // `client`, not `apiKey`: the singleton is initialized before React
    // renders (`initPostHog` in `main.tsx`, see `@/lib/posthogClient`), and the
    // provider only hands it to `usePostHog()`.
    <PostHogProvider client={posthog}>
      {/* Gated on the key, because an uninitialized PostHog does not drop the
          logs handed to it — it queues each one and retries a send that
          `_send_request` short-circuits on `__loaded`, backing off but never
          succeeding, for the life of the tab. Without a key there is nowhere
          for logs to go, so the sink is better left unregistered: `logger`
          then keeps its dev-console mirror and costs nothing in a build. */}
      {POSTHOG_API_KEY && <PostHogLoggerBridge />}
      {/* MathJax v4 singleton context — engine + fonts self-hosted (see
          `MathJaxProvider`). Mount once at root so any `<MathJax>` inline
          in a descendant (Hand-Calc formulas, future report equations) can
          typeset without each consumer re-instantiating the runtime. The
          engine itself lazy-loads on first `<MathJax>` mount. */}
      <MathJaxProvider>
        {/* The command registry, mounted at the ROOT rather than in the `_app`
            shell, and for a concrete reason: `ViewerToolbar` owns the `viewer`
            scope and calls `useCommandScope` unconditionally, and it is
            reachable from outside `_app` — `/share/study/$studyId` renders
            `Report` → `FeaSetupSection` → `ViewerToolbar` for an FEA study with
            boundary conditions. With the provider one level down, an anonymous
            share visitor got a thrown error instead of a report.
            The hooks throw rather than no-op on purpose (a silently dead
            shortcut is the hardest failure here to notice), which makes the
            provider's reach the thing that has to be right.

            Inside the router, so `useNavigate` still works for the navigation
            commands. Global HANDLERS stay in `_app` (`<AppCommands />`) — only
            the registry needs to be app-wide. */}
        <CommandsProvider>
          <Outlet />
        </CommandsProvider>
      </MathJaxProvider>
    </PostHogProvider>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: () => <Navigate to="/" />,
});
